// src/api/centralBillingApi.ts
//
// Central-direct billing RTK Query API. Browser calls Central directly with
// the user's Bearer token — no proxy through the CRM backend. All paths come
// from the installed suite-standard `@levelup/central-billing@1.0.0` package:
//
//   SUBSCRIPTION_ME_PATH  = "/api/subscription/me"
//   WALLET_BALANCE_PATH   = "/api/wallet/balance"
//   BILLING_PORTAL_PATH   = "/api/billing/stripe/portal"
//
// Response shapes come from the same package's type exports
// (`SubscriptionInfo`, `PortalSessionResponse`). No path or shape in this
// file is guessed — each one is the authoritative suite-standard contract.

import { createApi } from '@reduxjs/toolkit/query/react';
import {
  SUBSCRIPTION_ME_PATH,
  BILLING_PORTAL_PATH,
  type SubscriptionInfo,
  type PortalSessionResponse,
} from '@levelup/central-billing';
import { createCentralBaseQuery } from './centralBaseQuery';

// Central credit-balance path. NOT from the shared package: the shared
// `WALLET_BALANCE_PATH` (`/api/wallet/balance`) is not yet deployed on the
// Central instances CRM targets (returns 404). The server-side CRM code
// calls `/api/credits/balance` with the user's Bearer token — see
// `server/src/services/centralContext.ts:1294-1295` where that exact path
// has been exercised in production via `getCreditBalance`. We use the same
// verified path here for the browser-direct call.
const CREDITS_BALANCE_PATH = '/api/credits/balance';

export interface CentralWalletBalance {
  balance: number;
  reserved: number;
  available: number;
}

export const centralBillingApi = createApi({
  reducerPath: 'centralBillingApi',
  baseQuery: createCentralBaseQuery(),
  tagTypes: ['CentralSubscription', 'CentralWallet'],
  endpoints: (builder) => ({
    // GET /api/subscription/me
    getCentralSubscription: builder.query<SubscriptionInfo, void>({
      query: () => ({ url: SUBSCRIPTION_ME_PATH, method: 'GET' }),
      providesTags: ['CentralSubscription'],
    }),

    // GET /api/credits/balance?workspaceId=...
    getCentralWalletBalance: builder.query<CentralWalletBalance, { workspaceId: string }>({
      query: ({ workspaceId }) => ({
        url: CREDITS_BALANCE_PATH,
        method: 'GET',
        params: { workspaceId },
      }),
      transformResponse: (raw: Partial<CentralWalletBalance> | undefined) => ({
        balance: raw?.balance ?? 0,
        reserved: raw?.reserved ?? 0,
        available: raw?.available ?? raw?.balance ?? 0,
      }),
      providesTags: (_r, _e, { workspaceId }) => [
        { type: 'CentralWallet' as const, id: workspaceId },
      ],
    }),

    // POST /api/billing/stripe/portal  → { url }
    createCentralBillingPortalSession: builder.mutation<
      PortalSessionResponse,
      { returnUrl?: string }
    >({
      query: ({ returnUrl }) => ({
        url: BILLING_PORTAL_PATH,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: returnUrl ? { returnUrl } : {},
      }),
    }),
  }),
});

export const {
  useGetCentralSubscriptionQuery,
  useGetCentralWalletBalanceQuery,
  useCreateCentralBillingPortalSessionMutation,
} = centralBillingApi;
